import * as React from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'

export default function SingleSelectWithTextInput({
    selectItems = [],
    label,
    initialValue = '',
    onChange,
    id,
    placeholder = '',
}) {
    const [value, setValue] = React.useState(initialValue) // this state holds the current value for the chip
    const [inputValue, setInputValue] = React.useState('') // this state holds the current value within the text input

    const handleChange = (event, newValue) => {
        // Check if the newValue is not null or undefined
        if (newValue && newValue.trim() !== '') {
            setValue(newValue) // set the new value for the chip
            setInputValue('') // clear the text input

            if (onChange) {
                onChange(newValue) // propagate the change upward
            }
        } else if (newValue === null) {
            // Handle the case where the chip is removed or the selection is cleared
            setValue('') // clear the chip

            if (onChange) {
                onChange('') // propagate the change upward as clearing the selection
            }
        }
    }

    const handleInputChange = (event, newInputValue, reason) => {
        // Only react to actual typing (reason === 'input'); ignore 'reset'/'clear'
        // events fired on blur (clearOnBlur) so the typed text is preserved.
        if (reason === 'input') {
            setInputValue(newInputValue)

            if (onChange) {
                // Propagate the typed text upward so the graph can be submitted
                // even if the user didn't press Enter to turn it into a chip.
                onChange(newInputValue)
            }
        }
    }

    const handleDeleteChip = () => {
        setValue('') // clear the chip
        setInputValue('') // clear the text input field as well
        if (onChange) {
            onChange('') // propagate the change upward as clearing the selection
        }
    }

    const handleClickChip = () => {
        setInputValue(value) // move the chip value into the text input for editing
        setValue('') // clear the chip
        if (onChange) {
            onChange(value) // keep the value submittable while it's being edited
        }
    }

    React.useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    return (
        <Box>
            <Autocomplete
                value={value} // the current selected value
                onChange={handleChange} // handle changes in selection
                inputValue={inputValue} // the value within the text input
                onInputChange={handleInputChange} // handle changes in the text input
                id={id}
                options={selectItems}
                freeSolo // allow free-solo entries (entries not in the original list of options)
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                renderInput={params => (
                    <TextField
                        {...params}
                        variant="outlined"
                        placeholder={placeholder}
                        label={label}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    {value && !inputValue ? (
                                        <Chip
                                            label={value}
                                            onDelete={handleDeleteChip}
                                            onClick={handleClickChip}
                                            size="medium"
                                            style={{
                                                marginRight: 6,
                                                fontSize: '1rem',
                                                padding: '10px 20px',
                                            }}
                                        />
                                    ) : null}
                                    {params.InputProps.startAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
        </Box>
    )
}
